import { useEffect, useState } from 'react';
import { useMapInstance } from 'shared/map-exports';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import { SOURCE_ID } from './LightningLayer.constants';
import { isLightningSourceLoadedEvent } from './LightningLayer.utils';

// Missing from mapbox's error event type
type SourceErrorDetails = {
  sourceId?: string;
  tile?: unknown;
};

/**
 * True once the lightning source has loaded after `enabled` turns on. Stays
 * true until `enabled` turns off; later reloads don't reset it.
 */
export const useLightningSourceLoaded = (
  enabled: boolean,
  onError: (error: Error) => void,
): boolean => {
  const mapInstance = useMapInstance();
  const [isLoaded, setIsLoaded] = useState(false);
  const isWaiting = enabled && !isLoaded;

  useEffect(() => {
    if (!enabled) setIsLoaded(false);
  }, [enabled]);

  useMapEvent(
    'sourcedata',
    (event) => {
      if (isLightningSourceLoadedEvent(event)) setIsLoaded(true);
    },
    mapInstance,
    isWaiting,
  );

  useMapEvent(
    'error',
    (event) => {
      const { sourceId, tile } = event as SourceErrorDetails;
      if (sourceId !== SOURCE_ID) return;
      if (tile) {
        // Failed tiles fire no sourcedata
        setIsLoaded(true);
      } else {
        // TileJSON failed, nothing will load
        onError(event.error);
      }
    },
    mapInstance,
    isWaiting,
  );

  return enabled && isLoaded;
};
